import isEmpty from 'lodash/isEmpty';

import configLib from '../../../configLib';
import { GWQueryCase } from '../../models/gateway/types';
import { QueryCacheItemType } from '../../models/trading/types';
import { dataQuery } from '../../store/gateway';
import { REST_RETRIES_QUICK_DELAY } from '../../store/gateway/constants';
import { WebSocketEventData } from '../custom-event/types';
import { LoggerService } from '../logger/Logger.service';
import { GatewayRequestType, GatewayResponseType } from '../web-socket/types';

import { BaseSubscriptionService } from './BaseSubscription.service';
import { DEFAULT_NEWS_PAGE_NUMBER, DEFAULT_NEWS_PAGE_SIZE } from './constants';
import { SymbolNewsSubscriptionRequestType } from './types';

const LOG_PREFIX = '[S-NEWS-SUBS]';
const logger = LoggerService.getLogger();

/**
 * Symbol News Subscription Service
 */
export class SymbolNewsSubscriptionService extends BaseSubscriptionService<SymbolNewsSubscriptionRequestType> {
  private static instance: SymbolNewsSubscriptionService;

  // Make the constructor private, to have a single instance
  // eslint-disable-next-line no-useless-constructor
  private constructor() {
    super();
  }

  static getInstance(): SymbolNewsSubscriptionService {
    if (!this.instance) {
      this.instance = new SymbolNewsSubscriptionService();
    }

    return this.instance;
  }

  public subscribe(symbol: string, size?: number, page?: number): void {
    const pageSize = size ?? DEFAULT_NEWS_PAGE_SIZE;
    const pageNumber = page ?? DEFAULT_NEWS_PAGE_NUMBER;

    const requestBySymbol = this.getRequestBySymbol(symbol, pageSize, pageNumber);

    if (!requestBySymbol) {
      const request = {
        pageSize,
        pageNumber,
        symbols: symbol,
        ...this.getDefaultRequestParams(),
      };
      this.subscripitonRequests.push(request);
      this.gatewayService.sendRequest(request);
      return;
    }

    if (requestBySymbol.symbols.includes(symbol)) { // This symbol already in the subscription request list!
      return;
    }

    requestBySymbol.symbols = symbol;// add symbol to releated list
    this.gatewayService.sendRequest({
      pageSize,
      pageNumber,
      symbols: symbol,
      ...this.getDefaultRequestParams(),
    });
  }

  public subscribeList = (symbol: string[]): void => {
    // BE does not support a symbol list subscription! Please subscribe symbol news as single
    throw new Error('Not implemented yet!');
  };

  public unsubscribe(symbol: string): void {
    const requestBySymbol = this.getRequestBySymbol(symbol);
    if (!requestBySymbol) { // This symbol is not in the list!
      return;
    }

    if (this.shouldDebounce(requestBySymbol)) {
      return;
    }

    this.subscripitonRequests = this.subscripitonRequests.filter(item => item.symbols !== symbol);
  }

  public unsubscribeList = (symbol: string[]): void => {
    // there is no such kind of need right now
    throw new Error('Not implemented yet!');
  };

  // TODO: HW-1162  Cover all cases with unittesting
  public handleResponseEvent(event: WebSocketEventData<any>): void {
    const data = event.data ?? {};

    const {
      accessId,
      traceIdentifier,
    } = data || {};
    const traceIdentifierRequest = (this.gatewayService.getRequestedObjectList().find(
      item => item.messageType === this.getSubscriptionRequestType()
        && item.traceIdentifier === traceIdentifier,
    ) ?? {}) as SymbolNewsSubscriptionRequestType;

    const symbol = traceIdentifierRequest.symbols;
    if (isEmpty(symbol)) {
      logger.warn(`${LOG_PREFIX} no symbol found!`);
      return;
    }

    const cacheData: QueryCacheItemType = {
      queryCase: GWQueryCase.News,
      symbol,
      requestData: traceIdentifierRequest,
    };

    configLib.store.dispatch(dataQuery({
      accessId,
      traceIdentifier,
      estimatedDeliveryInSec: REST_RETRIES_QUICK_DELAY,
      cacheData,
    }));
  }

  public getSubscriptionRequestType = (): GatewayRequestType => GatewayRequestType.GetLatestNewsArticlesBySymbols;

  public getUnsubscriptionRequestType = (): GatewayRequestType => GatewayRequestType.None;

  protected getResponseType = (): GatewayResponseType => GatewayResponseType.DataQueryResponse;

  private getRequestBySymbol(
    symbol: string,
    pageSize?: number,
    pageNumber?: number,
  ): SymbolNewsSubscriptionRequestType | undefined {
    return this.subscripitonRequests.find(
      item => {
        const symbolMatch = item.symbols === symbol;
        if (pageSize !== undefined && pageNumber !== undefined) {
          return symbolMatch && item.pageSize === pageSize && item.pageNumber === pageNumber;
        }
        return symbolMatch;
      },
    );
  }
}
